/**
 * Setup Photos Actor
 *
 * Loads existing consumer photos from the database for returning users.
 * Maps photo types to context fields for OCR pre-population.
 * Returns neverthrow Result with typed success/error.
 */

import { downloadConsumerPhoto } from "@/app/directories/checkout/components/intake/consumerPhotosHelper";
import { datadogLogs } from "@datadog/browser-logs";
import { PhotoType } from "@frontend/lib/graphql/generated-gql";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getConsumerPhotos } from "../../server-actions/user-actions";

/**
 * Input Schema (What this actor needs)
 */
const SetupPhotosInputSchema = z.object({
  consumerId: z.string(),
  orgId: z.number(),
});

export type SetupPhotosInput = z.infer<typeof SetupPhotosInputSchema>;

/**
 * Photo metadata from database (for expiration checking)
 */
const ExistingPhotoMetadataSchema = z.object({
  id: z.string(),
  type: z.string(),
  ocrMetadata: z.record(z.unknown()).nullable().optional(),
  linkedInsuranceId: z.string().nullable().optional(),
  associatedPhotoId: z.string().nullable().optional(),
  insurancePriority: z.number().nullable().optional(),
});

export type ExistingPhotoMetadata = z.infer<typeof ExistingPhotoMetadataSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const SetupPhotosSuccessSchema = z.object({
  licensePhotoFront: z.string().optional(),
  licensePhotoBack: z.string().optional(),
  primaryInsurancePhotoFront: z.string().optional(),
  primaryInsurancePhotoBack: z.string().optional(),
  secondaryInsurancePhotoFront: z.string().optional(),
  secondaryInsurancePhotoBack: z.string().optional(),
  // Existing photo metadata for expiration checking in upload actor
  existingPhotos: z.array(ExistingPhotoMetadataSchema).optional(),
});

export type SetupPhotosSuccess = z.infer<typeof SetupPhotosSuccessSchema>;

/**
 * Error Schema (What this actor returns on failure)
 */
const SetupPhotosErrorSchema = z.object({
  code: z.enum([
    "PHOTO_FETCH_FAILED",
    "GRAPHQL_ERROR",
    "NETWORK_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

export type SetupPhotosError = z.infer<typeof SetupPhotosErrorSchema>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Setup Photos Actor Implementation
 *
 * NOTE: XState compatibility - we unwrap neverthrow Results here:
 * - throw error → XState onError transition
 * - return value → XState onDone transition
 */
const setupPhotosActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: SetupPhotosInput;
  }): Promise<SetupPhotosSuccess> => {
    const { consumerId, orgId } = input;

    console.log("[setupPhotosActor] Loading photos for consumer:", consumerId);

    try {
      const photos = await getConsumerPhotos(consumerId, orgId);

      const output: SetupPhotosSuccess = {
        existingPhotos: [],
      };

      // Download each photo and convert to data URL
      for (const photo of photos) {
        if (!photo.id) continue;

        // Download photo and get raw blob
        const downloadResult = await downloadConsumerPhoto(
          photo.id,
          consumerId,
          orgId,
        );

        if (!downloadResult?.response) {
          console.warn(
            "[setupPhotosActor] Failed to download photo:",
            photo.id,
          );
          continue;
        }

        // Convert blob to data URL (matches format of newly captured photos)
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(downloadResult.response);
        });

        // Collect photo metadata for expiration checking
        const photoMetadata: ExistingPhotoMetadata = {
          id: photo.id,
          type: photo.type,
          ocrMetadata: photo.ocrMetadata as Record<string, unknown> | null,
          linkedInsuranceId: photo.linkedInsuranceId,
          associatedPhotoId: photo.associatedPhotoId,
          insurancePriority: photo.insurance?.priority ?? null,
        };
        output.existingPhotos?.push(photoMetadata);

        // Map data URL to context field based on type and insurance priority
        switch (photo.type) {
          case PhotoType.IdCardFront:
            output.licensePhotoFront = dataUrl;
            break;

          case PhotoType.IdCardBack:
            output.licensePhotoBack = dataUrl;
            break;

          case PhotoType.InsuranceCardFront: {
            // Check insurance priority to determine primary vs secondary
            const isPrimary = photo.insurance?.priority === 1;
            if (isPrimary) {
              output.primaryInsurancePhotoFront = dataUrl;
            } else {
              output.secondaryInsurancePhotoFront = dataUrl;
            }
            break;
          }

          case PhotoType.InsuranceCardBack: {
            // Check insurance priority to determine primary vs secondary
            const isPrimary = photo.insurance?.priority === 1;
            if (isPrimary) {
              output.primaryInsurancePhotoBack = dataUrl;
            } else {
              output.secondaryInsurancePhotoBack = dataUrl;
            }
            break;
          }

          default:
            // Ignore other photo types (ProfilePhoto, Other)
            break;
        }
      }

      console.log("[setupPhotosActor] Mapped photos:", {
        licensePhotoFront: !!output.licensePhotoFront,
        licensePhotoBack: !!output.licensePhotoBack,
        primaryInsurancePhotoFront: !!output.primaryInsurancePhotoFront,
        primaryInsurancePhotoBack: !!output.primaryInsurancePhotoBack,
        secondaryInsurancePhotoFront: !!output.secondaryInsurancePhotoFront,
        secondaryInsurancePhotoBack: !!output.secondaryInsurancePhotoBack,
      });

      return output; // XState onDone
    } catch (error) {
      datadogLogs.logger.warn("[setupPhotosActor] Failed to load photos", {
        error: error instanceof Error ? error.message : String(error),
      });

      // Throw for XState onError transition
      const errorObj: SetupPhotosError = {
        code: "PHOTO_FETCH_FAILED",
        message:
          error instanceof Error ? error.message : "Failed to fetch photos",
        retryable: true, // Network/GraphQL errors are retryable
      };

      throw errorObj;
    }
  },
);

/**
 * Setup Photos Actor with Metadata
 */
export const setupPhotosActor: ActorWithMetadata = {
  impl: setupPhotosActorImpl,
  metadata: {
    displayName: "setupPhotos",
    description:
      "Load existing consumer photos for returning users to pre-populate OCR fields",
    category: "data",
    retryable: true,
    timeout: 10000, // 10 seconds
    errorCodes: [
      "PHOTO_FETCH_FAILED",
      "GRAPHQL_ERROR",
      "NETWORK_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: SetupPhotosInputSchema,
    successSchema: SetupPhotosSuccessSchema,
    errorSchema: SetupPhotosErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as SetupPhotosSuccess;
      return {
        licensePhotoFront: success.licensePhotoFront,
        licensePhotoBack: success.licensePhotoBack,
        primaryInsurancePhotoFront: success.primaryInsurancePhotoFront,
        primaryInsurancePhotoBack: success.primaryInsurancePhotoBack,
        secondaryInsurancePhotoFront: success.secondaryInsurancePhotoFront,
        secondaryInsurancePhotoBack: success.secondaryInsurancePhotoBack,
        existingPhotos: success.existingPhotos,
      };
    },
    onError: (error: unknown) => {
      const err = error as SetupPhotosError;
      return {
        setupPhotosError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
